import { Cartesian3, Ray, } from "cesium";
import { AssetType } from "../../types";
import { ViewerCamera } from "../../viewers/viewer-camera";
export var CesiumPointSelectorType;
(function (CesiumPointSelectorType) {
    CesiumPointSelectorType["Default"] = "default";
    CesiumPointSelectorType["IgnoreImage"] = "ignoreImage";
    //prioritizes in order of pointcloud,reference topology,drawing2dimage
    CesiumPointSelectorType["PriorityBased"] = "priorityBased";
    //rayCast against specified IRayCastCapable object
    CesiumPointSelectorType["RayCastBased"] = "rayCastBased";
})(CesiumPointSelectorType || (CesiumPointSelectorType = {}));
/**
 * Default implementation of the point selector.
 * It simply selects the point as per cesium
 */
export class DefaultCesiumPointSelector {
    type = CesiumPointSelectorType.Default;
    _viewer;
    _handler;
    x;
    constructor(args) {
        this._viewer = args.viewer;
    }
    async getPointOnMousePos(mousePos) {
        try {
            const pickResult = this._viewer.scene.pick(mousePos);
            return this._viewer.scene.pickPosition(mousePos);
        }
        catch (e) {
            return undefined;
        }
    }
}
export class IgnoreImageCesiumPointSelector {
    type = CesiumPointSelectorType.IgnoreImage;
    _viewer;
    _viewerCamera;
    _handler;
    _site;
    constructor(args) {
        this._viewer = args.viewer;
        this._site = args.site;
        this._viewerCamera = ViewerCamera.getInstance(this._viewer);
    }
    async getPointOnMousePos(mousePos) {
        //Idea no 1: Use cesium utils
        // pick and if it is image entity
        // what if there is mulltiple images
        // make images unpickable and if nothing gets picked and then try to pick the image
        try {
            const images = this._site
                .getVisibleRayCastCapables()
                .get(AssetType.REFERENCE_TOPOLOGY);
            //check if any other is pickable and if not then pick the image
            // if pickable get the point closest to the camera
            const rayCastCapablesWithoutImage = [];
            this._site.getVisibleRayCastCapables().forEach((value, key) => {
                if (key !== AssetType.REFERENCE_TOPOLOGY) {
                    rayCastCapablesWithoutImage.push(...value);
                }
            });
            const ray = this._viewerCamera.getPickRay(mousePos);
            const nextPoint = Cartesian3.add(ray.origin, ray.direction, new Cartesian3());
            const localPoints = await this._site.convertPointsToLocalSpace([
                ray.origin,
                nextPoint,
            ]);
            const localDir = Cartesian3.subtract(localPoints[1], localPoints[0], new Cartesian3());
            Cartesian3.normalize(localDir, localDir);
            const localRay = new Ray(localPoints[0], localDir);
            let hits = [];
            if (rayCastCapablesWithoutImage.length > 0) {
                const unfilteredHits = rayCastCapablesWithoutImage.map((rc, index) => rc.rayCast(localRay));
                hits = unfilteredHits.filter((hit) => hit !== undefined);
            }
            if (hits.length === 0) {
                //check if image is visible
                if (images.length > 0) {
                    //pick from image
                    const unfilteredHits = images.map((rc, index) => rc.rayCast(localRay));
                    hits = unfilteredHits.filter((hit) => hit !== undefined);
                }
            }
            if (hits.length > 0) {
                const closestHit = hits.reduce((prev, current) => {
                    return Cartesian3.distance(ray.origin, prev) <
                        Cartesian3.distance(ray.origin, current)
                        ? prev
                        : current;
                }, hits[0]);
                const WGS84Point = await this._site.convertPointsToWGS84([closestHit]);
                return WGS84Point[0];
            }
            const pickResult = this._viewer.scene.pick(mousePos);
            return this._viewer.scene.pickPosition(mousePos);
        }
        catch (e) {
            return undefined;
        }
    }
}
export class PriorityBasedCesiumPointSelector {
    type = CesiumPointSelectorType.PriorityBased;
    _viewer;
    _viewerCamera;
    _handler;
    _site;
    constructor(args) {
        this._viewer = args.viewer;
        this._site = args.site;
        this._viewerCamera = ViewerCamera.getInstance(this._viewer);
    }
    async getPointOnMousePos(mousePos) {
        try {
            // get all visible terrain
            // sort them by prority of pointcloud,reference topology,drawing2dimage
            const ray = this._viewerCamera.getPickRay(mousePos);
            if (!ray) {
                return undefined;
            }
            const nextPoint = Cartesian3.add(ray.origin, ray.direction, new Cartesian3());
            const localPoints = await this._site.convertPointsToLocalSpace([
                ray.origin,
                nextPoint,
            ]);
            const localDir = Cartesian3.subtract(localPoints[1], localPoints[0], new Cartesian3());
            Cartesian3.normalize(localDir, localDir);
            const localRay = new Ray(localPoints[0], localDir);
            const visibleTerrains = this._site.getVisibleRayCastCapables();
            const pointClouds = visibleTerrains.get(AssetType.Point_Cloud) ?? [];
            let hit = pointClouds.length > 0 ? pointClouds[0].rayCast(localRay) : undefined;
            if (!hit) {
                const referenceTopologies = visibleTerrains.get(AssetType.REFERENCE_TOPOLOGY) ?? [];
                hit =
                    referenceTopologies.length > 0
                        ? referenceTopologies[0].rayCast(localRay)
                        : undefined;
            }
            if (!hit) {
                const drawing2dImages = visibleTerrains.get(AssetType.Design_Data) ?? [];
                hit =
                    drawing2dImages.length > 0
                        ? drawing2dImages[0].rayCast(localRay)
                        : undefined;
            }
            if (hit) {
                const WGS84Point = await this._site.convertPointsToWGS84([hit]);
                return WGS84Point[0];
            }
        }
        catch (e) {
            return undefined;
        }
        return undefined;
    }
}
/**
 * RayCast-based point selector.
 * Casts a ray against a specified IRayCastCapable object (e.g., Road, Trench)
 */
export class RayCastBasedPointSelector {
    type = CesiumPointSelectorType.RayCastBased;
    _viewer;
    _viewerCamera;
    _site;
    constructor(args) {
        this._viewer = args.viewer;
        this._site = args.site;
        this._viewerCamera = ViewerCamera.getInstance(this._viewer);
    }
    async getPointOnMousePos(mousePos, rayCaster) {
        if (!rayCaster) {
            // Fallback to default pick when no rayCaster specified
            try {
                return this._viewer.scene.pickPosition(mousePos);
            }
            catch (e) {
                return undefined;
            }
        }
        try {
            // Get ray from camera
            const ray = this._viewerCamera.getPickRay(mousePos);
            if (!ray) {
                return undefined;
            }
            // Convert ray to local space
            const nextPoint = Cartesian3.add(ray.origin, ray.direction, new Cartesian3());
            const localPoints = await this._site.convertPointsToLocalSpace([
                ray.origin,
                nextPoint,
            ]);
            const localDir = Cartesian3.subtract(localPoints[1], localPoints[0], new Cartesian3());
            Cartesian3.normalize(localDir, localDir);
            const localRay = new Ray(localPoints[0], localDir);
            // RayCast against the specified object
            const hit = rayCaster.rayCast(localRay);
            if (!hit) {
                return undefined;
            }
            // Convert back to WGS84
            const wgs84Points = await this._site.convertPointsToWGS84([hit]);
            return wgs84Points[0];
        }
        catch (e) {
            return undefined;
        }
    }
}
export class CesiumPointSelectorFactory {
    _selectorArgs;
    _selectorMap;
    constructor(args) {
        this._selectorArgs = args;
        this._selectorMap = new Map();
    }
    createPointSelector(type, args) {
        switch (type) {
            case CesiumPointSelectorType.Default:
                return new DefaultCesiumPointSelector(args);
            case CesiumPointSelectorType.IgnoreImage:
                return new IgnoreImageCesiumPointSelector(args);
            case CesiumPointSelectorType.PriorityBased:
                return new PriorityBasedCesiumPointSelector(args);
            case CesiumPointSelectorType.RayCastBased:
                return new RayCastBasedPointSelector(args);
            default:
                return new DefaultCesiumPointSelector(args);
        }
    }
    getPointSelector(type) {
        if (!this._selectorMap.has(type)) {
            this._selectorMap.set(type, this.createPointSelector(type, this._selectorArgs));
        }
        return this._selectorMap.get(type);
    }
}
//# sourceMappingURL=point-selector.js.map